import { useEffect, useState, useMemo } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle2, Circle, Plus, Trash2, Calendar, Video, MapPin, Clock,
  LogOut, Sparkles, ListChecks, Palette, Award,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/lib/auth";
import { myBookings, myGoals, createGoalApi, updateGoalApi, deleteGoalApi } from "@/lib/api";

const GOAL_COLORS = [
  { id: "gold", swatch: "#E0B72C", border: "border-gold", bg: "bg-gold/15", text: "text-oxblood" },
  { id: "terracotta", swatch: "#CC5E30", border: "border-terracotta", bg: "bg-terracotta/15", text: "text-terracotta" },
  { id: "oxblood", swatch: "#6B1818", border: "border-oxblood", bg: "bg-oxblood/15", text: "text-oxblood" },
  { id: "cognac", swatch: "#9B5215", border: "border-cognac", bg: "bg-cognac/15", text: "text-cognac" },
  { id: "sage", swatch: "#6B7F5A", border: "border-[#6B7F5A]", bg: "bg-[#6B7F5A]/15", text: "text-[#3D4D2E]" },
  { id: "plum", swatch: "#6B3A6E", border: "border-[#6B3A6E]", bg: "bg-[#6B3A6E]/15", text: "text-[#4A2950]" },
];

const colorFor = (id) => GOAL_COLORS.find((c) => c.id === id) || GOAL_COLORS[0];

export default function Profile() {
  const { user, loading, logout } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [goals, setGoals] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [newGoal, setNewGoal] = useState({ title: "", description: "", color: "gold" });

  useEffect(() => {
    if (!user) return;
    Promise.all([myBookings(), myGoals()])
      .then(([b, g]) => { setBookings(b); setGoals(g); })
      .catch(() => toast.error("Could not load your profile data."))
      .finally(() => setLoadingData(false));
  }, [user]);

  const completedGoals = useMemo(() => goals.filter((g) => g.done).length, [goals]);
  const progress = goals.length ? Math.round((completedGoals / goals.length) * 100) : 0;

  const upcomingBookings = useMemo(() => bookings.filter((b) => b.status === "confirmed"), [bookings]);
  const pastMeetings = useMemo(() => bookings.filter((b) => b.status === "completed"), [bookings]);

  // assign meeting numbers based on date order
  const meetingNumbers = useMemo(() => {
    const all = [...bookings].filter((b) => b.status !== "cancelled").sort((a, b) => a.preferred_date.localeCompare(b.preferred_date));
    const map = {};
    all.forEach((b, i) => { map[b.id] = i + 1; });
    return map;
  }, [bookings]);

  const toggleGoal = async (g) => {
    try {
      const updated = await updateGoalApi(g.id, { done: !g.done });
      setGoals((prev) => prev.map((x) => (x.id === g.id ? updated : x)));
    } catch {
      toast.error("Could not update goal.");
    }
  };

  const changeColor = async (g, color) => {
    try {
      const updated = await updateGoalApi(g.id, { color });
      setGoals((prev) => prev.map((x) => (x.id === g.id ? updated : x)));
    } catch {
      toast.error("Could not update color.");
    }
  };

  const addGoal = async (e) => {
    e.preventDefault();
    if (!newGoal.title.trim()) return;
    try {
      const created = await createGoalApi(newGoal);
      setGoals((prev) => [...prev, created]);
      setNewGoal({ title: "", description: "", color: "gold" });
      toast.success("Goal added.");
    } catch {
      toast.error("Could not add goal.");
    }
  };

  const removeGoal = async (id) => {
    try {
      await deleteGoalApi(id);
      setGoals((prev) => prev.filter((g) => g.id !== id));
    } catch {
      toast.error("Could not remove goal.");
    }
  };

  if (loading) return <div className="min-h-screen bg-ivory" />;
  if (!user) return <Navigate to="/login" state={{ from: "/me" }} replace />;

  return (
    <div className="min-h-screen bg-ivory">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 md:px-12 pt-10 md:pt-16 pb-24" data-testid="profile-page">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <span className="label-overline">Your private space</span>
            <h1 className="font-serif text-5xl text-oxblood mt-3">Hello, {user.full_name.split(" ")[0]}.</h1>
            <p className="text-inkSoft mt-3">Track your progress, see your meetings, and book follow-ups.</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/book" className="btn-primary" data-testid="profile-book-link">Book a follow-up</Link>
            <button onClick={logout} className="btn-ghost" data-testid="profile-logout">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>

        {loadingData ? (
          <div className="mt-12 h-40 bg-cream/40 animate-pulse" />
        ) : (
          <div className="mt-12 grid lg:grid-cols-12 gap-8">
            {/* GOALS COLUMN */}
            <section className="lg:col-span-7" data-testid="goals-section">
              <div className="flex items-end justify-between mb-6">
                <div>
                  <span className="label-overline flex items-center gap-2"><ListChecks size={12} /> Financial progress</span>
                  <h2 className="font-serif text-3xl text-oxblood mt-2">Your goals</h2>
                </div>
                <div className="text-right">
                  <p className="font-serif text-4xl text-cognac">{completedGoals}<span className="text-inkMute text-2xl">/{goals.length}</span></p>
                  <p className="text-[10px] tracking-[0.22em] uppercase text-inkMute mt-1">{progress}% complete</p>
                </div>
              </div>

              {/* progress bar */}
              <div className="h-1 bg-cream/80 mb-8 overflow-hidden">
                <div className="h-full bg-gold transition-all duration-700" style={{ width: `${progress}%` }} />
              </div>

              <div className="space-y-2" data-testid="goals-list">
                {goals.map((g) => {
                  const c = colorFor(g.color);
                  return (
                    <div key={g.id} className={`border ${c.border} ${c.bg} p-5 transition-all`} data-testid={`goal-${g.id}`}>
                      <div className="flex items-start gap-4">
                        <button onClick={() => toggleGoal(g)} className="mt-0.5" data-testid={`toggle-goal-${g.id}`}>
                          {g.done ? <CheckCircle2 size={22} className={c.text} /> : <Circle size={22} className="text-inkMute" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className={`font-serif text-lg leading-snug ${g.done ? "line-through opacity-60" : ""} ${c.text}`}>
                            {g.title}
                          </p>
                          {g.description && (
                            <p className="text-sm text-inkSoft mt-1">{g.description}</p>
                          )}
                          <div className="mt-3 flex items-center gap-2 flex-wrap">
                            {g.is_default && (
                              <span className="text-[10px] tracking-[0.22em] uppercase text-inkMute">Starter goal</span>
                            )}
                            <div className="flex items-center gap-1" data-testid={`goal-colors-${g.id}`}>
                              <Palette size={10} className="text-inkMute" />
                              {GOAL_COLORS.map((col) => (
                                <button
                                  key={col.id}
                                  type="button"
                                  onClick={() => changeColor(g, col.id)}
                                  className={`w-4 h-4 rounded-full border ${g.color === col.id ? "border-oxblood ring-2 ring-oxblood/30" : "border-cognac/30"}`}
                                  style={{ backgroundColor: col.swatch }}
                                  aria-label={`Color ${col.id}`}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                        {!g.is_default && (
                          <button onClick={() => removeGoal(g.id)} className="text-inkMute hover:text-terracotta transition-colors" data-testid={`delete-goal-${g.id}`}>
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Add goal */}
              <form onSubmit={addGoal} className="mt-8 border border-cognac/15 p-5 bg-cream/40" data-testid="add-goal-form">
                <p className="label-overline flex items-center gap-2"><Plus size={12} /> Add your own goal</p>
                <input
                  type="text"
                  value={newGoal.title}
                  onChange={(e) => setNewGoal({ ...newGoal, title: e.target.value })}
                  className="input-flat mt-3"
                  placeholder="e.g. Save $1,000 by August"
                  data-testid="new-goal-title"
                />
                <input
                  type="text"
                  value={newGoal.description}
                  onChange={(e) => setNewGoal({ ...newGoal, description: e.target.value })}
                  className="input-flat mt-3"
                  placeholder="A note about why this matters (optional)"
                  data-testid="new-goal-description"
                />
                <div className="flex items-center gap-3 mt-5">
                  <Palette size={14} className="text-inkMute" />
                  {GOAL_COLORS.map((col) => (
                    <button
                      key={col.id}
                      type="button"
                      onClick={() => setNewGoal({ ...newGoal, color: col.id })}
                      className={`w-6 h-6 rounded-full border ${newGoal.color === col.id ? "border-oxblood ring-2 ring-oxblood/30" : "border-cognac/30"}`}
                      style={{ backgroundColor: col.swatch }}
                      data-testid={`new-goal-color-${col.id}`}
                      aria-label={`Color ${col.id}`}
                    />
                  ))}
                  <button type="submit" disabled={!newGoal.title.trim()} className="ml-auto btn-primary disabled:opacity-40" data-testid="new-goal-submit">
                    Add goal
                  </button>
                </div>
              </form>
            </section>

            {/* MEETINGS COLUMN */}
            <section className="lg:col-span-5 space-y-6" data-testid="meetings-section">
              <div>
                <span className="label-overline flex items-center gap-2"><Calendar size={12} /> Upcoming</span>
                <h2 className="font-serif text-3xl text-oxblood mt-2">Your meetings</h2>
              </div>

              {upcomingBookings.length === 0 ? (
                <div className="border border-cognac/15 bg-cream/40 p-8 text-center" data-testid="no-upcoming">
                  <p className="text-inkSoft">No upcoming meeting.</p>
                  <Link to="/book" className="btn-ghost mt-4 inline-flex">Book one</Link>
                </div>
              ) : (
                upcomingBookings.map((b) => (
                  <article key={b.id} className="border border-gold/40 bg-gold/5 p-6" data-testid={`upcoming-${b.id}`}>
                    <p className="label-overline flex items-center gap-2"><Sparkles size={12} className="text-gold" /> Meeting #{meetingNumbers[b.id]}</p>
                    <p className="font-serif text-2xl text-oxblood mt-2">{b.preferred_date}</p>
                    <p className="text-cognac">{b.preferred_time}</p>
                    <p className="mt-3 inline-flex items-center gap-1 text-xs text-inkMute">
                      {b.meeting_mode === "virtual" ? <><Video size={12} /> Virtual</> : <><MapPin size={12} /> In person</>}
                    </p>
                    <p className="mt-3 text-sm text-inkSoft">{b.custom_topic || b.topic}</p>
                  </article>
                ))
              )}

              {pastMeetings.length > 0 && (
                <>
                  <div className="pt-4">
                    <span className="label-overline flex items-center gap-2"><Award size={12} /> History</span>
                    <h2 className="font-serif text-3xl text-oxblood mt-2">Past meetings</h2>
                  </div>
                  {pastMeetings.map((b) => (
                    <article key={b.id} className="border border-cognac/15 bg-creamAlt/50 p-6 space-y-4" data-testid={`past-${b.id}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="label-overline">Meeting #{meetingNumbers[b.id]}</p>
                          <p className="font-serif text-xl text-oxblood mt-1">{b.preferred_date} · {b.preferred_time}</p>
                          <p className="text-xs text-cognac mt-1">{b.custom_topic || b.topic}</p>
                        </div>
                        {b.meeting_duration_minutes ? (
                          <p className="inline-flex items-center gap-1 text-xs text-inkMute">
                            <Clock size={12} /> {b.meeting_duration_minutes} min
                          </p>
                        ) : null}
                      </div>

                      {b.meeting_summary && (
                        <MeetingBlock label="Recap" body={b.meeting_summary} />
                      )}
                      {b.things_achieved && (
                        <MeetingBlock label="What we accomplished" body={b.things_achieved} />
                      )}
                      {b.goals_for_next_meeting && (
                        <MeetingBlock label="For next time" body={b.goals_for_next_meeting} />
                      )}
                      {b.shared_notes && (
                        <MeetingBlock label="Notes from Aisha" body={b.shared_notes} />
                      )}
                      {!b.meeting_summary && !b.things_achieved && !b.goals_for_next_meeting && !b.shared_notes && (
                        <p className="text-sm text-inkMute italic">Aisha hasn't added notes for this meeting yet.</p>
                      )}
                    </article>
                  ))}
                </>
              )}
            </section>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

function MeetingBlock({ label, body }) {
  return (
    <div className="border-l-2 border-gold/60 pl-4">
      <p className="label-overline">{label}</p>
      <p className="text-sm text-oxblood mt-1 whitespace-pre-wrap leading-relaxed">{body}</p>
    </div>
  );
}
